import type { ModalProps } from '@workramp/ui';
import type { AdvancedCreateFromFileSettings } from '../../types';
import {
  Button,
  Col,
  FileField,
  Icon,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Stack,
  TextField,
  Tooltip,
  useToast,
} from '@workramp/ui';
import { useState } from 'react';
import { I18n } from '~/common/explicit-exports';
import AssetPickerTypes from '~/components/common/asset-picker/asset-picker-types';
import { useActionCableSubscription } from '~/hooks';
import { hideLoader, MixPanel, showLoader } from '~/common';
import isFeatureEnabled from '~/components/common/is-feature-enabled';
import {
  MAX_NAME_LENGTH,
  MIXPANEL_CREATE_FROM_FILE_CANCEL,
  MIXPANEL_CREATE_FROM_FILE_CREATE,
  MIXPANEL_CREATE_FROM_FILE_UPLOAD,
} from '../../constants';
import { useCreateGuideFromFileMutation } from '../../api';
import { DEFAULT_ADVANCED_SETTINGS, VALID_FILE_EXTENSIONS } from '../constants';
import { CreateFromFileSettings } from '../CreateFromFileSettings';
import { StyledCharacterLimit, TooltipLink, Description } from './CreateFromFileModal.styles';

export interface CreateFromFileModalProps extends Omit<ModalProps, 'children' | 'isOpen'> {
  isOpen: boolean;
  itemFolderId?: string;
  initialName?: string;
  initialAdvancedSettings?: AdvancedCreateFromFileSettings;
  onStartBlank: (name: string) => void;
}

const HelpTooltipComponent = (helpLink) => {
  const TRANSLATED_HELP_TOOLTIP_TEXT = I18n.t('js.admin.content_creation.advanced_settings.help_tooltip_text');
  const TRANSLATED_HELP_TOOLTIP_LINK_TEXT = I18n.t(
    'js.admin.content_creation.advanced_settings.help_tooltip_link_text',
  );
  return (
    <>
      {TRANSLATED_HELP_TOOLTIP_TEXT}
      &nbsp;
      <TooltipLink href={helpLink} target="_blank" autoFocus>
        {TRANSLATED_HELP_TOOLTIP_LINK_TEXT}
      </TooltipLink>
    </>
  );
};

export function CreateFromFileModal({
  initialName = '',
  itemFolderId,
  initialAdvancedSettings = DEFAULT_ADVANCED_SETTINGS,
  onStartBlank,
  onClose,
  ...modalProps
}: CreateFromFileModalProps) {
  const UPLOADED_FILE_LIMIT = 5;
  const TRANSLATED_CANCEL = I18n.t('js.common.modal.cancel');
  const TRANSLATED_CREATE = I18n.t('js.admin.content_creation.create');
  const TRANSLATED_START_BLANK = I18n.t('js.admin.content_creation.start_blank');
  const TRANSLATED_TITLE = I18n.t('js.admin.content_creation.create_from_file_title');
  const TRANSLATED_SINGLE_FILE_DESCRIPTION = I18n.t('js.admin.content_creation.create_from_file_description');
  const TRANSLATED_MULTI_FILE_DESCRIPTION = I18n.t('js.admin.content_creation.create_from_multiple_files_description');
  const TRANSLATED_FILE_LABEL = I18n.t('js.admin.content_creation.create_from_file_label');
  const TRANSLATED_UPLOAD_LABEL = I18n.t('js.admin.content_creation.create_from_file_upload_label');
  const TRANSLATED_UPLOAD_DESCRIPTION = I18n.t('js.admin.content_creation.create_from_file_upload_description');
  const TRANSLATED_ADVANCED_SETTINGS = I18n.t('js.admin.content_creation.advanced_settings.title');
  const TRANSLATED_PLACEHOLDER = I18n.t('js.admin.content_creation.create_modal_placeholder', {
    content: AssetPickerTypes.getDisplayName(AssetPickerTypes.GUIDE),
  });
  const TRANSLATED_LABEL = I18n.t('js.admin.content_creation.create_modal_label', {
    content: AssetPickerTypes.getDisplayName(AssetPickerTypes.GUIDE),
  });
  const HELP_LINK = 'https://help.workramp.com/en/articles/8008338-ai-assist#h_aeb079bc68';

  const { open: openToast } = useToast();

  const [name, setname] = useState(initialName);
  const [sourceFile, setSourceFile] = useState<File | undefined>();
  const [sourceFiles, setSourceFiles] = useState<File[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [advancedSettings, setAdvancedSettings] = useState(initialAdvancedSettings);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  const showAdvancedSettingsButton = isFeatureEnabled('fy26_q1_advanced_ai_create_from_file');

  const isMultiFile = isFeatureEnabled('fy26-q2-generate-guide-from-multiple-files');

  const TRANSLATED_DESCRIPTION = isMultiFile ? TRANSLATED_MULTI_FILE_DESCRIPTION : TRANSLATED_SINGLE_FILE_DESCRIPTION;

  const createFromFileMutation = useCreateGuideFromFileMutation();

  const handleGenerateGuide = (settings: AdvancedCreateFromFileSettings = advancedSettings) => {
    MixPanel.track(MIXPANEL_CREATE_FROM_FILE_CREATE, { settings });

    if (!name || (!sourceFile && sourceFiles.length === 0)) {
      return;
    }

    createFromFileMutation.mutate(
      {
        title: name || 'Generated Guide',
        itemFolderId,
        files: isMultiFile ? sourceFiles : [sourceFile],
        advancedSettings: settings,
      },
      {
        onSuccess: () => {
          showLoader(false, false, '&nbsp;&nbsp;Extracting content...', true);
          setIsCreating(true);
        },
      },
    );
  };

  const isInProgress = createFromFileMutation.isLoading || isCreating;

  useActionCableSubscription(
    {
      channel: 'Courses::GenerationInitiatedChannel',
    },
    {
      received: (data) => {
        if (data.error) {
          openToast({
            children: data.error,
          });

          setIsCreating(false);
          hideLoader();
          return;
        }

        window.location.href = `/admin/task_versions/${data.id}/preview`;
      },
    },
    {
      isEnabled: isInProgress,
    },
  );

  const isDisabled = (!sourceFile && sourceFiles.length === 0) || isInProgress;

  const renderUploader = () => {
    if (isMultiFile) {
      // Display a FileField for each existing file and another for uploading new files,
      // up to 5 files total.
      return (
        <>
          {sourceFiles.length < UPLOADED_FILE_LIMIT && (
            <FileField
              key={`uploader-${sourceFiles.length}`}
              label={TRANSLATED_FILE_LABEL}
              buttonLabel={TRANSLATED_UPLOAD_LABEL}
              hideLabel
              value={undefined}
              onChange={(file) => {
                MixPanel.track(MIXPANEL_CREATE_FROM_FILE_UPLOAD, { totalFiles: sourceFiles.length + 1 });
                const updatedFiles = [...sourceFiles, file].slice(0, UPLOADED_FILE_LIMIT);
                setSourceFiles(updatedFiles);
              }}
              accept={VALID_FILE_EXTENSIONS}
            />
          )}
          {sourceFiles.map((existingFile, index) => (
            <FileField
              key={`${existingFile.name}-${existingFile.lastModified}`}
              label={TRANSLATED_FILE_LABEL}
              buttonLabel={TRANSLATED_UPLOAD_LABEL}
              hideLabel
              value={existingFile}
              onChange={(file) => {
                MixPanel.track(MIXPANEL_CREATE_FROM_FILE_UPLOAD, { totalFiles: sourceFiles.length + 1 });
                const updatedFiles = sourceFiles.map((f, i) => (i === index ? file : f));
                setSourceFiles(updatedFiles);
              }}
              onRemove={() => {
                MixPanel.track(MIXPANEL_CREATE_FROM_FILE_UPLOAD, {
                  totalFiles: sourceFiles.length - 1,
                });
                const updatedFiles = sourceFiles.filter((_, i) => i !== index);
                setSourceFiles(updatedFiles);
              }}
              accept={VALID_FILE_EXTENSIONS}
            />
          ))}
        </>
      );
    }

    // For single file upload, just use one FileField.
    return (
      <FileField
        label={TRANSLATED_FILE_LABEL}
        buttonLabel={TRANSLATED_UPLOAD_LABEL}
        hideLabel
        value={sourceFile}
        onChange={(file) => {
          MixPanel.track(MIXPANEL_CREATE_FROM_FILE_UPLOAD);
          setSourceFile(file);
        }}
        accept={VALID_FILE_EXTENSIONS}
      />
    );
  };

  return (
    <Modal size="sm" onClose={isInProgress ? undefined : onClose} {...modalProps}>
      {showAdvancedSettings ? (
        <CreateFromFileSettings
          onNavigateBack={() => setShowAdvancedSettings(false)}
          isCreating={isInProgress}
          initialAdvancedSettings={advancedSettings}
          updateSettings={setAdvancedSettings}
        />
      ) : (
        <>
          <ModalHeader title={TRANSLATED_TITLE} description={TRANSLATED_DESCRIPTION} />
          <ModalBody>
            <Stack size={6}>
              {!isMultiFile && (
                <div>
                  <TextField
                    label={TRANSLATED_LABEL}
                    placeholder={TRANSLATED_PLACEHOLDER}
                    maxLength={MAX_NAME_LENGTH}
                    value={name}
                    onChange={setname}
                    isDisabled={isInProgress}
                  />
                  <StyledCharacterLimit size={0}>
                    {I18n.t('js.admin.guide.description_character_limit', {
                      count: name.length,
                      max: MAX_NAME_LENGTH,
                    })}
                  </StyledCharacterLimit>
                </div>
              )}
              <div>
                <Label weight="semibold">{TRANSLATED_FILE_LABEL}</Label>
                <Description size={1} color="secondary">
                  {TRANSLATED_UPLOAD_DESCRIPTION}
                </Description>
                {renderUploader()}
              </div>
              {showAdvancedSettingsButton && (
                <div>
                  <Button
                    size="lg"
                    variant="text-inline"
                    isDisabled={isInProgress}
                    onPress={() => setShowAdvancedSettings(true)}
                  >
                    {TRANSLATED_ADVANCED_SETTINGS}
                  </Button>
                  &nbsp;
                  <Tooltip message={HelpTooltipComponent(HELP_LINK)}>
                    <Icon name="info:regular" size="match-font" />
                  </Tooltip>
                </div>
              )}
            </Stack>
          </ModalBody>
        </>
      )}
      <ModalFooter>
        <Row align="center" gapX={2}>
          <Col>
            <Button
              size="lg"
              variant="outline"
              color="secondary"
              isDisabled={isInProgress}
              onPress={() => onStartBlank(name)}
            >
              {TRANSLATED_START_BLANK}
            </Button>
          </Col>
          <Col xs="auto">
            <Button
              size="lg"
              variant="text"
              color="secondary"
              isDisabled={isInProgress}
              onPress={() => {
                MixPanel.track(MIXPANEL_CREATE_FROM_FILE_CANCEL);
                onClose?.();
              }}
            >
              {TRANSLATED_CANCEL}
            </Button>
          </Col>
          <Col xs="auto">
            <Button size="lg" isDisabled={isDisabled} onPress={() => handleGenerateGuide()}>
              {TRANSLATED_CREATE}
            </Button>
          </Col>
        </Row>
      </ModalFooter>
    </Modal>
  );
}
